import type {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { API_CONFIG } from './config';
import { store } from '@/store/store';
import { clearAuth } from '@/store/slices/authSlice';
import type {
  FileCsvResponse,
  FileUploadResponse,
} from '@/features/files/types/file.types';

axios.defaults.withCredentials = true;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  private static instance: ApiService;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      ...API_CONFIG,
      validateStatus: (status) => status >= 200 && status < 300,
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('token');
          store.dispatch(clearAuth());
          return Promise.reject(
            this.handleError(
              error,
              "Vous n'êtes pas autorisé à accéder à cette page."
            )
          );
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError, customMessage?: string): ApiError {
    if (error.response) {
      const responseData = error.response.data as { detail?: string };
      return new ApiError(
        responseData.detail || customMessage || 'Erreur serveur',
        error.response.status,
        error.response.data
      );
    }
    if (error.request) {
      return new ApiError('Aucune réponse du serveur', 500);
    }
    return new ApiError(error.message || 'Erreur de requête', 500);
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  public async getBlob(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<FileCsvResponse> {
    const response: AxiosResponse<Blob> = await this.axiosInstance.get(url, {
      ...config,
      responseType: 'blob',
    });

    let filename = 'docteller.csv';
    const contentDisposition = response.headers['content-disposition'] as
      | string
      | undefined;

    if (contentDisposition) {
      const match = /filename="([^"]+)"/.exec(contentDisposition);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const contentType = response.headers['content-type'] as string | undefined;

    return {
      blob: new Blob([response.data], {
        type: contentType,
      }),
      filename,
    };
  }

  public async getBlobWithoutFilename(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.axiosInstance.get(url, {
      ...config,
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] as string | undefined;

    return new Blob([response.data], {
      type: contentType,
    });
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, config);
    return response.data;
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  public async upload(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AxiosResponse<FileUploadResponse>> {
    const formData = new FormData();
    formData.append('payload', file);

    return this.axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
  }

  public async download(url: string, filename: string): Promise<void> {
    const response = await this.axiosInstance.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const apiService = ApiService.getInstance();
